import { useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { RouteObject } from 'react-router-dom'
import { AccessLevelEnum } from 'src/utils/config/config'
import { find, uniq } from 'src/utils/config/lodashUtils'
import { getAccessLevel } from 'src/utils/functions/accessLevel'
import { getOrgRoutesInArrayList } from 'src/utils/functions/liveRoutes'
import { getLocal, setLocal } from 'src/utils/functions/localStorage'
import { useAuth } from './AuthContext'
import { useErrorData } from './ErrorContext'

export interface RouteCategoryType {
  uid: string
  name: string
  key: string
  routes: string[]
  showInSidebar: boolean
}

export interface CategorizedRoutesType {
  category: {
    name: string
    key: string
    uid?: string
    routes: string[]
    showInSidebar: boolean
  }
  routes: {
    icon: { label: string; value: string }
    label: string
    key: string
    subNav: { label: string; key: string }[]
    uid: string
    showInSidebar: boolean
    hidden?: boolean
    type?: string
    pageTitle?: string
    tooltip?: string
    created_at?: string
  }[]
  showInSidebar: boolean
}

interface PrivateLayoutDrawerContextInterface {
  theme: any
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  routes: any[]
  categoryOrder: string[]
  toggleDrawer: () => void
  privateDrawerChildren?: React.ReactNode
  setPrivateDrawerChildren: React.Dispatch<React.SetStateAction<React.ReactNode>>
  privateRoutes: RouteObject[]
  setPrivateRoutes: React.Dispatch<React.SetStateAction<RouteObject[]>>
  categorizedRoutes: CategorizedRoutesType[]
}

const PrivateLayoutDrawerContext = React.createContext({} as PrivateLayoutDrawerContextInterface)

export const usePrivateLayoutDrawerContext = (): PrivateLayoutDrawerContextInterface => React.useContext(PrivateLayoutDrawerContext)

interface PrivateLayoutDrawerContextProviderProps {
  children: React.ReactNode
}

export default function PrivateLayoutDrawerContextProvider(props: PrivateLayoutDrawerContextProviderProps): JSX.Element {
  const theme: any = useTheme()
  const { currentUser, orgConfig, globalRoutes, getRoutes, routeCategories, categoryOrder } = useAuth()
  const { handleError } = useErrorData()
  const [open, setDrawerOpen] = React.useState(true)
  // const [routeLoading, setRouteLoading] = React.useState(false)
  // const [routes, setRoutes] = React.useState([])
  const [privateDrawerChildren, setPrivateDrawerChildren] = React.useState<React.ReactNode>()
  const [privateRoutes, setPrivateRoutes] = React.useState<PrivateLayoutDrawerContextInterface['privateRoutes']>([])
  const sm = useMediaQuery(theme.breakpoints.down('md'))

  const setOpen = (value: boolean) => {
    setDrawerOpen(value)
    setLocal('drawer_open', value)
  }

  const toggleDrawer = () => setOpen(!open)

  React.useEffect(() => {
    if (getLocal('drawer_open') !== null) {
      setOpen(getLocal('drawer_open'))
    }
  }, [])

  React.useEffect(() => {
    if (sm === true) setOpen(false)
  }, [sm])

  const getRouteObject = (uid, routes) => {
    const route = find(routes, { uid })
    return route
  }

  function getOrgRouteObject(orgRoutes, uid) {
    return orgRoutes.find((route) => typeof route === 'object' && route.uid === uid)
  }

  const routes = React.useMemo(() => {
    try {
      const tempLevel = getAccessLevel(currentUser)
      const accessLevel: AccessLevelEnum = Array.isArray(tempLevel) ? tempLevel[0] : tempLevel
      let tempAccessLevel = Object.keys(orgConfig?.access_levels || {}).includes(accessLevel) ? accessLevel : AccessLevelEnum.BUSINESS_ADMIN

      let tempRoutes = []
      if (tempAccessLevel != AccessLevelEnum.DEMO) {
        tempRoutes = getRoutes(tempAccessLevel)
      } else {
        tempRoutes = getRoutes(AccessLevelEnum.BUSINESS_ADMIN)
      }

      tempRoutes = getOrgRoutesInArrayList(tempRoutes).map((item) => {
        let orgRouteObject = getOrgRouteObject(tempRoutes, item)
        if (typeof item === 'string') {
          let routeObject = getRouteObject(item, globalRoutes)

          return { ...routeObject, ...orgRouteObject }
        } else if (typeof item === 'object') {
          return {
            ...getRouteObject(item.uid, globalRoutes),
            hidden: true,
            ...orgRouteObject
          }
        }
        return item
      })

      const uniqRoutes = uniq(tempRoutes).filter((item) => item !== undefined)
      return uniqRoutes
    } catch (error) {
      handleError(error.message)
      return []
    }
  }, [currentUser, orgConfig, globalRoutes, getRoutes])

  const categorizedRoutes = React.useMemo(() => {
    if (!categoryOrder || categoryOrder.length === 0) return []
    const result: CategorizedRoutesType[] = []
    const routesWithCategories = []
    categoryOrder.forEach((categoryUid) => {
      const category = routeCategories.find((item) => item.key === categoryUid)
      if (category) {
        const assignedRoutes = []
        category.routes.forEach((routeUid) => {
          const route = routes.find((item) => item.uid === routeUid)
          if (route && route.showInSidebar && Object.keys(route).length > 0) {
            assignedRoutes.push(route)
            routesWithCategories.push(route.uid)
          }
        })
        if (assignedRoutes.length)
          result.push({
            category: { ...category },
            routes: assignedRoutes,
            showInSidebar: category.showInSidebar
          })
      }
    })
    const routesWithoutCategories = routes.filter((route) => route.showInSidebar && !routesWithCategories.includes(route.uid) && Object.keys(route).length > 0)
    if (routesWithoutCategories.length)
      result.push({
        category: {
          name: 'Others',
          key: 'others',
          routes: routesWithoutCategories.map((route) => route.uid),
          showInSidebar: true
        },
        routes: routesWithoutCategories,
        showInSidebar: true
      })

    return result
  }, [routeCategories, categoryOrder, routes])

  const contextValue = React.useMemo(() => {
    return {
      theme,
      open,
      setOpen,
      routes,
      categoryOrder,
      toggleDrawer,
      privateDrawerChildren,
      setPrivateDrawerChildren,
      privateRoutes,
      setPrivateRoutes,
      categorizedRoutes
    }
  }, [routes, categoryOrder, theme, open, privateDrawerChildren, privateRoutes, categorizedRoutes])

  return <PrivateLayoutDrawerContext.Provider value={contextValue}>{props.children}</PrivateLayoutDrawerContext.Provider>
}
